<template>
    <DraggableNotes :note="logContainer" @update:note="updateLogContainerPosition">
        <div v-if="show" v-resize @stopResize="updateSize"
            class="absolute flex flex-col w-full z-[100] bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-2xl overflow-hidden"
            :style="{ left: logContainer.x + 'px', top: logContainer.y + 'px', width: logContainer.width + 'px', height: logContainer.height + 'px' }">
            
            <!-- Header -->
            <div class="flex items-center justify-between px-4 py-3 bg-gradient-to-r from-indigo-500 to-purple-500 handle active:cursor-grabbing cursor-move">
                <div class="flex items-center space-x-2">
                    <div class="w-2 h-2 bg-white rounded-full animate-pulse"></div>
                    <h3 class="text-white font-semibold text-sm">Event Log</h3>
                    <span v-if="eventLog.length > 0" class="px-2 py-0.5 bg-white/20 rounded-full text-xs text-white font-medium">
                        {{ eventLog.length }}
                    </span>
                </div>
                <div class="flex items-center space-x-2">
                    <button 
                        @click="clearLog" 
                        class="p-1.5 rounded-md hover:bg-white/20 transition-colors group"
                        title="Clear log"
                    >
                        <TrashIcon class="size-5 text-white group-hover:text-red-200 transition-colors" />
                    </button>
                    <button 
                        @click="close" 
                        class="p-1.5 rounded-md hover:bg-white/20 transition-colors group"
                        title="Close"
                    >
                        <XMarkIcon class="size-5 text-white group-hover:text-red-200 transition-colors" />
                    </button>
                </div>
            </div>

            <!-- Event Log Content -->
            <div class="flex-1 overflow-y-auto bg-gray-50 dark:bg-gray-900/50 custom-scrollbar" style="max-height: calc(100% - 60px);">
                <div v-if="eventLog.length === 0" class="flex flex-col items-center justify-center h-full py-12 px-4">
                    <div class="w-16 h-16 bg-gray-200 dark:bg-gray-700 rounded-full flex items-center justify-center mb-4">
                        <ArrowsPointingOutIcon class="size-8 text-gray-400 dark:text-gray-500" />
                    </div>
                    <p class="text-gray-500 dark:text-gray-400 text-sm font-medium">No events logged yet</p>
                    <p class="text-gray-400 dark:text-gray-500 text-xs mt-1 text-center">Events will appear here as they occur</p>
                </div>
                
                <div v-else class="p-3 space-y-2">
                    <div 
                        v-for="(event, index) in eventLog" 
                        :key="index"
                        class="group relative bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-3 hover:shadow-md transition-all duration-200 hover:border-indigo-300 dark:hover:border-indigo-600"
                    >
                        <div class="flex items-start space-x-3">
                            <!-- Event Indicator -->
                            <div class="flex-shrink-0 mt-0.5">
                                <div class="w-2 h-2 rounded-full bg-indigo-500 group-hover:bg-indigo-600 transition-colors"></div>
                            </div>
                            
                            <!-- Event Content -->
                            <div class="flex-1 min-w-0">
                                <div class="flex items-start justify-between gap-2">
                                    <div class="flex-1 min-w-0">
                                        <div class="text-sm font-medium text-gray-900 dark:text-white break-words">
                                            <pre v-if="typeof event.message === 'object'" class="whitespace-pre-wrap text-xs font-mono bg-gray-50 dark:bg-gray-900/50 p-2 rounded border border-gray-200 dark:border-gray-700 overflow-x-auto">{{ JSON.stringify(event.message, null, 2) }}</pre>
                                            <span v-else class="text-sm">{{ event.message }}</span>
                                        </div>
                                        <div class="flex items-center mt-2 space-x-2">
                                            <div class="flex items-center text-xs text-gray-500 dark:text-gray-400">
                                                <svg class="w-3.5 h-3.5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                                                </svg>
                                                <span>{{ formatTimestamp(event.timestamp) }}</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Resizers -->
            <div class="resizer resizer-right hover:bg-indigo-500"></div>
            <div class="resizer resizer-bottom hover:bg-indigo-500"></div>
            <div class="resizer resizer-corner hover:bg-indigo-500"></div>
        </div>
    </DraggableNotes>
</template>

<script setup>
import { ref, onMounted, defineProps, watch } from 'vue' 
import appStore from '@/store/app.store'
import DraggableNotes from './elements/DraggableNotes.vue';
import { ArrowsPointingOutIcon, XMarkIcon, TrashIcon } from '@heroicons/vue/24/outline';

const props = defineProps({
    show: {
        type: Boolean,
        default: false
    }
});
const eventLog = ref([]);
const show = ref(props.show);
const logContainer = ref({
    x: window.innerWidth - 450,
    y: window.innerHeight - 550,
    width: 300,
    height: 500
});



onMounted(() => {
    eventLog.value = appStore.state.eventLog;
})


const updateLogContainerPosition = (updatedNote) => {
    logContainer.value.x = updatedNote.x;
    logContainer.value.y = updatedNote.y;
}

const updateSize = (event) => {
    logContainer.value.width = event.width;
    logContainer.value.height = event.height;
}

const close = () => {
    show.value = false;
}

const clearLog = () => {
    appStore.state.eventLog = [];
}

const formatTimestamp = (timestamp) => {
    if (!timestamp) {
        return new Date().toLocaleTimeString();
    }
    if (typeof timestamp === 'string') {
        return timestamp;
    }
    return new Date(timestamp).toLocaleTimeString();
}

watch(() => props.show, (newVal) => {
    show.value = newVal;
})

appStore.watch((state) => state.eventLog, (newVal) => {
    eventLog.value = newVal
})
</script>

<style scoped>
.custom-scrollbar::-webkit-scrollbar {
    width: 8px;
}

.custom-scrollbar::-webkit-scrollbar-track {
    background: transparent;
}

.custom-scrollbar::-webkit-scrollbar-thumb {
    background: rgba(99, 102, 241, 0.3);
    border-radius: 4px;
}

.custom-scrollbar::-webkit-scrollbar-thumb:hover {
    background: rgba(99, 102, 241, 0.5);
}

.dark .custom-scrollbar::-webkit-scrollbar-thumb {
    background: rgba(99, 102, 241, 0.4);
}

.dark .custom-scrollbar::-webkit-scrollbar-thumb:hover {
    background: rgba(99, 102, 241, 0.6);
}
</style>